import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSportsStandings, SPORT_LIST, formatPointValue, getTeamBorderColor, getTeamRankColor, getLegendItems } from "@/hooks/useSportsStandings";
import { usePointRanking } from "@/hooks/usePointRanking";
import { useTotalRanking } from "@/hooks/useTotalRanking";
import { useSiteStats } from "@/hooks/useSiteStats";

interface NoticeItem {
  id: number;
  title: string;
  date: string;
}

interface RankingItem {
  rank: number;
  name: string;
  points: number;
  color: string;
}

interface LeftSidebarProps {
  notices?: NoticeItem[];
  noticesLoading?: boolean;
}

export default function LeftSidebar({ notices: propNotices, noticesLoading }: LeftSidebarProps) {
  const notices = propNotices || [];

  const {
    rankings: pointRankings,
    loading: pointLoading,
    error: pointError,
    refreshRanking: refreshPointRanking,
  } = usePointRanking(10);

  const {
    rankings: totalRankings,
    loading: totalLoading,
    error: totalError,
    refreshRanking: refreshTotalRanking,
  } = useTotalRanking(10);

  const {
    stats: realStats,
    loading: statsLoading,
    error: statsError,
    refreshStats,
  } = useSiteStats();

  const displayStats = realStats;

  const {
    sportTab,
    leagueTab,
    displayRows,
    isLoading,
    hasError,
    useMock,
    currentSportConfig,
    rulesData,
    handleSportChange,
    setLeagueTab,
    retryFetch,
  } = useSportsStandings("축구");

  const [rankTab, setRankTab] = useState("총랭킹");
  const rankTabs = ["총랭킹", "포인트랭킹", "경험치랭킹"];

  return (
    <div className="flex flex-col gap-3">
      {/* 공지사항 */}
      <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 md:py-2.5 border-b border-background-300">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center text-primary-500">
              <i className="ri-megaphone-line" />
            </span>
            <h3 className="text-[11px] md:text-xs font-bold text-foreground-700">공지사항</h3>
          </div>
          <Link
            to="/notices"
            className="text-[10px] text-foreground-600 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            전체보기 &gt;
          </Link>
        </div>
        <div className="px-3 py-2">
          {noticesLoading ? (
            <div className="space-y-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5">
                  <div className="w-4 h-4 rounded bg-background-200 animate-pulse flex-shrink-0" />
                  <div className="flex-1 h-3 rounded bg-background-200 animate-pulse" />
                  <div className="w-8 h-3 rounded bg-background-200 animate-pulse flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : notices.length === 0 ? (
            <div className="py-6 text-center text-[11px] text-foreground-600">
              등록된 공지사항이 없습니다
            </div>
          ) : (
            notices.map((notice) => (
              <Link
                key={notice.id}
                to={`/notices?id=${notice.id}`}
                className="w-full flex items-center gap-2 py-1.5 border-b border-background-300/50 last:border-0 hover:bg-background-200/50 transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center text-[10px] bg-primary-500/20 text-primary-400 rounded flex-shrink-0">
                  {notice.id}
                </span>
                <span className="flex-1 text-[10px] md:text-[11px] text-foreground-600 truncate text-left">
                  {notice.title}
                </span>
                <span className="text-[10px] text-foreground-600 flex-shrink-0">{notice.date}</span>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* 종목별 리그순위 */}
      <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-3 py-2 md:py-2.5 border-b border-background-300">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center text-primary-500">
              <i className="ri-trophy-line" />
            </span>
            <h3 className="text-[11px] md:text-xs font-bold text-foreground-700">종목별 리그순위</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-accent-500" />
            <Link
              to={`/standings?sport=${encodeURIComponent(sportTab)}&league=${encodeURIComponent(leagueTab)}`}
              className="text-[10px] text-primary-400 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              더보기 &gt;
            </Link>
          </div>
        </div>

        {/* 종목 탭 */}
        <div className="flex items-center gap-1 px-2 pt-2 pb-1">
          {SPORT_LIST.map((sport) => (
            <button
              key={sport}
              onClick={() => handleSportChange(sport)}
              className={`px-2 py-1 text-[10px] rounded font-medium whitespace-nowrap transition-colors flex-shrink-0 cursor-pointer ${
                sportTab === sport
                  ? "bg-primary-500 text-white"
                  : "bg-background-200 text-foreground-600 hover:bg-background-300"
              }`}
            >
              {sport}
            </button>
          ))}
        </div>

        {/* 리그 탭 (2개 이상일 때만 표시) */}
        {currentSportConfig.leagues.length > 1 && (
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-background-300 overflow-x-auto">
            {currentSportConfig.leagues.map((league) => (
              <button
                key={league}
                onClick={() => setLeagueTab(league)}
                className={`px-2 py-0.5 text-[10px] rounded whitespace-nowrap transition-colors flex-shrink-0 cursor-pointer ${
                  leagueTab === league
                    ? "bg-primary-500/20 text-primary-400"
                    : "text-foreground-600 hover:text-foreground-400"
                }`}
              >
                {league}
              </button>
            ))}
          </div>
        )}

        {/* 컬럼 헤더 */}
        <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] text-foreground-600 border-b border-background-300 bg-background-50">
          <span className="w-5" />
          <span className="flex-1">팀명</span>
          <span className="w-8 text-center">{currentSportConfig.colLabels.points}</span>
          <span className="w-7 text-center">{currentSportConfig.colLabels.games}</span>
          <span className="w-5 text-center">{currentSportConfig.colLabels.wins}</span>
        </div>

        {/* 순위 테이블 */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-7 h-7 rounded-full border-2 border-background-200 border-t-primary-500 animate-spin" />
            <p className="mt-2 text-[11px] text-foreground-600">불러오는 중...</p>
          </div>
        ) : (
          <div className="max-h-56 md:max-h-64 overflow-y-auto">
            {displayRows.map((team) => {
              const borderColor = getTeamBorderColor(team.rank, leagueTab, rulesData || {});
              const rankColorClass = getTeamRankColor(team.rank, leagueTab, rulesData || {});
              const ptDisplay = formatPointValue(team.points, sportTab);

              return (
                <div
                  key={team.rank}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs border-b border-background-300/50 last:border-0 hover:bg-background-200/50 transition-colors ${borderColor}`}
                >
                  <span className={`w-4 text-center font-bold text-[10px] flex-shrink-0 ${rankColorClass}`}>
                    {team.rank}
                  </span>
                  <img
                    alt={team.team}
                    className="w-5 h-5 rounded-full object-contain flex-shrink-0"
                    src={team.logo}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='%23e5e7eb'/%3E%3C/svg%3E";
                    }}
                  />
                  <span className="flex-1 text-foreground-600 text-[11px] truncate">{team.team}</span>
                  <span className="text-foreground-700 font-bold w-8 text-center text-[10px]">{ptDisplay}</span>
                  <span className="text-foreground-600 w-7 text-center text-[10px]">{team.games}</span>
                  <span className="text-foreground-600 w-5 text-center text-[10px]">{team.wins}</span>
                </div>
              );
            })}

            {!isLoading && displayRows.length === 0 && (
              <div className="py-6 text-center text-[11px] text-foreground-600">
                <i className="ri-calendar-close-line text-lg" />
                <p className="mt-1">현재 시즌 순위가 없습니다</p>
              </div>
            )}
          </div>
        )}

        {/* 범례 */}
        {getLegendItems(leagueTab, rulesData || {}).length > 0 && (
          <div className="px-3 py-2 text-[9px] text-foreground-600 border-t border-background-300 flex flex-wrap gap-2">
            {getLegendItems(leagueTab, rulesData || {}).map((item) => (
              <span key={item.label} className="flex items-center gap-1">
                <span className={`w-1.5 h-1.5 ${item.color} rounded-sm flex-shrink-0`} />
                {item.label}
              </span>
            ))}
          </div>
        )}

        {/* 출처 */}
        <div className="px-3 py-1.5 text-[10px] text-foreground-600 border-t border-background-300 flex items-center justify-between">
          <span>
            {useMock && !isLoading
              ? `네이버 스포츠 ${leagueTab}`
              : `네이버 스포츠 ${leagueTab} (실시간)`}
          </span>
          {hasError && (
            <button
              onClick={retryFetch}
              className="text-[10px] text-primary-400 hover:text-primary-500 cursor-pointer whitespace-nowrap"
            >
              재시도
            </button>
          )}
        </div>
      </div>

      {/* 텔레그램 */}
      <div className="bg-gradient-to-br from-background-50 via-background-100 to-background-50 rounded-lg border border-background-500 overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] md:text-xs font-bold text-foreground-700">텔레그램 공식 채널</div>
            <div className="text-[10px] text-foreground-600 mt-0.5">월드컵티비 시즌2 최신 정보를 배달해드립니다</div>
            <a
              href="https://t.me/wdc79"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="mt-2 inline-block px-3 py-1 text-[11px] bg-[#0088CC] text-white rounded hover:bg-[#0077B3] transition-colors cursor-pointer whitespace-nowrap"
            >
              채널 바로가기
            </a>
          </div>
          <div className="relative w-10 h-10 flex-shrink-0">
            <div className="absolute inset-0 rounded-full border border-[#0088CC]/40 animate-ping" />
            <div className="absolute inset-0 rounded-full bg-[#0088CC] flex items-center justify-center z-10">
              <span className="flex items-center justify-center text-white text-[24px] leading-none">
                <i className="ri-telegram-fill" />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 랭킹 */}
      <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 md:py-2.5 border-b border-background-300">
          <span className="w-4 h-4 flex items-center justify-center text-primary-500">
            <i className="ri-vip-crown-line" />
          </span>
          <h3 className="text-[11px] md:text-xs font-bold text-foreground-700">월드컵티비 시즌2랭킹</h3>
        </div>
        <div className="flex items-center gap-1 px-2 py-2 border-b border-background-300 overflow-x-auto">
          {rankTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setRankTab(tab)}
              className={`px-2 py-0.5 text-[10px] rounded whitespace-nowrap transition-colors flex-shrink-0 cursor-pointer ${
                rankTab === tab
                  ? "bg-primary-500/20 text-primary-400"
                  : "text-foreground-600 hover:text-foreground-400"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="px-3 py-2">
          {rankTab === "포인트랭킹" ? (
            pointLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-6 h-6 rounded-full border-2 border-background-200 border-t-primary-500 animate-spin" />
                <p className="mt-2 text-[10px] text-foreground-600">랭킹 불러오는 중...</p>
              </div>
            ) : pointError ? (
              <div className="py-4 text-center">
                <p className="text-[10px] text-foreground-600 mb-2">{pointError}</p>
                <button
                  onClick={refreshPointRanking}
                  className="text-[10px] text-primary-400 hover:text-primary-500 cursor-pointer whitespace-nowrap"
                >
                  재시도
                </button>
              </div>
            ) : pointRankings.length > 0 ? (
              pointRankings.map((user) => {
                const rankColors: Record<number, string> = {
                  1: "text-yellow-500",
                  2: "text-gray-400",
                  3: "text-amber-600",
                };
                const rankColor = rankColors[user.rank] || "text-foreground-600";

                return (
                  <Link
                    key={user.id}
                    to={`/profile/${user.id}`}
                    className="flex items-center gap-2 py-1.5 border-b border-background-300/50 last:border-0 hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <span className={`w-5 text-center text-xs font-bold ${rankColor}`}>{user.rank}</span>
                    <img
                      alt={user.nickname}
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                      src="https://readdy.ai/api/search-image?query=default%20user%20avatar%20abstract%20minimal%20icon%20design%20neutral%20soft%20color%20circle&width=36&height=36&seq=user-avatar-real-1&orientation=squarish"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] md:text-xs text-foreground-600 truncate">{user.nickname}</div>
                      <div className="text-[10px] text-foreground-600">{user.points.toLocaleString()} P</div>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="py-6 text-center">
                <p className="text-[11px] text-foreground-600">데이터가 없습니다</p>
              </div>
            )
          ) : rankTab === "총랭킹" ? (
            totalLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-6 h-6 rounded-full border-2 border-background-200 border-t-primary-500 animate-spin" />
                <p className="mt-2 text-[10px] text-foreground-600">랭킹 불러오는 중...</p>
              </div>
            ) : totalError ? (
              <div className="py-4 text-center">
                <p className="text-[10px] text-foreground-600 mb-2">{totalError}</p>
                <button
                  onClick={refreshTotalRanking}
                  className="text-[10px] text-primary-400 hover:text-primary-500 cursor-pointer whitespace-nowrap"
                >
                  재시도
                </button>
              </div>
            ) : totalRankings.length > 0 ? (
              totalRankings.map((user) => {
                const rankColors: Record<number, string> = {
                  1: "text-yellow-500",
                  2: "text-gray-400",
                  3: "text-amber-600",
                };
                const rankColor = rankColors[user.rank] || "text-foreground-600";

                return (
                  <Link
                    key={user.id}
                    to={`/profile/${user.id}`}
                    className="flex items-center gap-2 py-1.5 border-b border-background-300/50 last:border-0 hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <span className={`w-5 text-center text-xs font-bold ${rankColor}`}>{user.rank}</span>
                    <img
                      alt={user.nickname}
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                      src="https://readdy.ai/api/search-image?query=default%20user%20avatar%20abstract%20minimal%20icon%20design%20neutral%20soft%20color%20circle&width=36&height=36&seq=user-avatar-real-1&orientation=squarish"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] md:text-xs text-foreground-600 truncate">{user.nickname}</div>
                      <div className="text-[10px] text-foreground-600">
                        {user.totalScore.toLocaleString()} 점
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="py-6 text-center">
                <p className="text-[11px] text-foreground-600">데이터가 없습니다</p>
              </div>
            )
          ) : (
            <div className="py-6 text-center">
              <p className="text-[11px] text-foreground-600">데이터가 없습니다</p>
            </div>
          )}
        </div>
      </div>

      {/* 통계 */}
      <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 md:py-2.5 border-b border-background-300">
          <span className="w-4 h-4 flex items-center justify-center text-primary-500">
            <i className="ri-bar-chart-box-line" />
          </span>
          <h3 className="text-[11px] md:text-xs font-bold text-foreground-700">월드컵티비 시즌2통계</h3>
        </div>
        <div className="px-3 py-3 space-y-2 md:space-y-2.5">
          {statsLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-5 h-5 rounded-full border-2 border-background-200 border-t-primary-500 animate-spin" />
              <p className="mt-2 text-[10px] text-foreground-600">통계 불러오는 중...</p>
            </div>
          ) : statsError ? (
            <div className="py-4 text-center">
              <p className="text-[10px] text-foreground-600 mb-2">{statsError}</p>
              <button
                onClick={refreshStats}
                className="text-[10px] text-primary-400 hover:text-primary-500 cursor-pointer whitespace-nowrap"
              >
                재시도
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] md:text-xs text-foreground-600">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line" /></span>
                  <span>오늘 방문자</span>
                </div>
                <span className="text-[11px] md:text-xs font-bold text-foreground-700">{displayStats.todayVisitors.toLocaleString()} 명</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] md:text-xs text-foreground-600">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-gamepad-line" /></span>
                  <span>어제 방문자</span>
                </div>
                <span className="text-[11px] md:text-xs font-bold text-foreground-700">{displayStats.yesterdayVisitors.toLocaleString()} 명</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] md:text-xs text-foreground-600">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500"><i className="ri-fire-line" /></span>
                  <span>최대 방문자</span>
                </div>
                <span className="text-[11px] md:text-xs font-bold text-primary-400">{displayStats.maxVisitors.toLocaleString()} 명</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] md:text-xs text-foreground-600">
                  <span className="w-4 h-4 flex items-center justify-center text-accent-500"><i className="ri-global-line" /></span>
                  <span>전체 방문자</span>
                </div>
                <span className="text-[11px] md:text-xs font-bold text-accent-400">{displayStats.totalVisitors.toLocaleString()} 명</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}